/**
 * Event travel reducer
 */
import transformTravelSnapshot from '../../utils/transformTravelSnapshot';
import { addHotelsRoomsCapacityProperty, transformHotelRoomDates,
  transformHotelAdvanceRules } from '../../utils/transformHotelData';
import {
  transformAirTravelData,
  populateCapacityForGroupFlights,
  getCapacityIdBasedOnTestMode
} from '../../utils/transformAirTravelData';
import { getGroupFlightsSnapshotData } from '../selectors/eventTravel';
import { defaultRegistrationTypeId } from '../../utils/registrationType';
import { map } from 'lodash';
import { getIn } from 'icepick';

export const REQUESTED_ACTION = {
  BOOK: 'BOOK',
  MODIFY: 'MODIFY',
  CANCEL: 'CANCEL'
};

const initialState = {
  hotelsData: {
    hotels: []
  },
  airData: {
    airRequestSetup: {},
    groupFlightSetup: {}
  }
};

export const LOAD_TRAVEL_SNAPSHOT = 'event-widgets/eventTravel/LOAD_TRAVEL_SNAPSHOT';
const LOAD_HOTELS = 'event-widgets/eventTravel/LOAD_HOTELS';
const LOAD_AIR_TRAVEL = 'event-widgets/eventTravel/LOAD_AIR_TRAVEL';
const LOAD_GROUP_FLIGHTS = 'event-widgets/eventTravel/LOAD_GROUP_FLIGHTS';
const LOAD_GROUP_FLIGHTS_CAPACITY = 'event-widgets/eventTravel/LOAD_GROUP_FLIGHTS_CAPACITY';

export default function reducer(state = initialState, action) {
  const { type, payload = {} } = action;
  switch (type) {
    case LOAD_TRAVEL_SNAPSHOT:
      return {
        ...state,
        ...payload,
        hotelsData: {
          ...state.hotelsData,
          ...payload.hotelsData
        },
        airData: {
          ...state.airData,
          ...payload.airData
        }
      };
    case LOAD_HOTELS:
      return {
        ...state,
        hotelsData: {
          ...state.hotelsData,
          hotels: [
            ...payload.hotels
          ],
          hotelRequestRules: payload.hotelRequestRules ? [
            ...payload.hotelRequestRules
          ] : []
        }
      };
    case LOAD_AIR_TRAVEL:
      return {
        ...state,
        airData: {
          ...state.airData,
          airRequestSetup: {
            ...payload.airRequestSetup
          },
          airActualSetup: {
            ...payload.airActualSetup
          }
        }
      };
    case LOAD_GROUP_FLIGHTS:
      return {
        ...state,
        airData: {
          ...state.airData,
          groupFlightSetup: {
            ...payload.groupFlightSetup
          }
        }
      };
    case LOAD_GROUP_FLIGHTS_CAPACITY:
      return {
        ...state,
        airData: {
          ...state.airData,
          groupFlightSetup: {
            ...state.airData.groupFlightSetup,
            capacityMap: {
              ...payload.capacityMap
            }
          }
        }
      };
    default:
      return state;
  }
}

/**
 * creates a travel snapshot action
 * @param travelSnapshot travelSnapshot
 * @param travelSnapshotVersion version of travel snapshot
 * @param accountHotels hotels from the accountSnapshot
 * @returns {{type: string, payload: {travelSnapshotVersion: *, hotelsData: {hotels}, airData: {}}}}
 */
const createLoadTravelSnapshotAction = (travelSnapshot, travelSnapshotVersion, accountHotels, isInTestMode) => {
  const transformedSnapshot = transformTravelSnapshot(travelSnapshot, accountHotels, isInTestMode);
  return {
    type: LOAD_TRAVEL_SNAPSHOT,
    payload: {
      travelSnapshotVersion,
      hotelsData: {
        ...transformedSnapshot.hotelsData
      },
      airData: {
        ...transformedSnapshot.airData
      }
    }
  };
};

/**
 * Add hotels rooms capacities per date per contact type to rooms.
 * @param hotels
 * @param capacityMap
 * @param isInTestMode
 * @param isPlanner
 * @returns {{type: string, payload: {hotels: *}}}
 */
const createActionToLoadHotelsRoomsCapacities = (hotels, hotelRequestRules, capacityMap, isInTestMode, isPlanner) => {
  return {
    type: LOAD_HOTELS,
    payload: {
      hotels: addHotelsRoomsCapacityProperty(hotels, capacityMap, isInTestMode, isPlanner),
      hotelRequestRules
    }
  };
};

/**
 * Transforms hotel properties as required on UI
 * TODO : Can be removed later after code sweep on UI
 * @param {Array} hotels
 * @returns {{type: string, payload: {hotels: Object[]}}}
 */
const createLoadHotelRoomDatesAction = (hotels, hotelRequestRules, eventTimezone) => {
  return {
    type: LOAD_HOTELS,
    payload: {
      hotels: transformHotelRoomDates(hotels, eventTimezone),
      hotelRequestRules: transformHotelAdvanceRules(hotelRequestRules, eventTimezone)
    }
  };
};

/**
 * Transforms air travel properties as required on UI
 * @param {Object} airRequestSetup
 * @param {Object} airActualSetup
 * @returns {{type: string, payload: {airRequestSetup: Object, airActualSetup: Object}}}
 */
const createLoadAirTravelDataAction = ({ airRequestSetup, airActualSetup }) => {
  return {
    type: LOAD_AIR_TRAVEL,
    payload: {
      airRequestSetup: transformAirTravelData(airRequestSetup),
      airActualSetup: transformAirTravelData(airActualSetup)
    }
  };
};

const createLoadGroupFlightsCapacityAction = (groupFlightCapacityIds, capacityMap, isPlanner) => {
  return {
    type: LOAD_GROUP_FLIGHTS_CAPACITY,
    payload: {
      capacityMap: populateCapacityForGroupFlights(groupFlightCapacityIds, capacityMap, isPlanner)
    }
  };
};

/**
 * Function to get all hotel room capacity id's for passing into bulk end point.
 * @param {Array} hotels
 * @param {Boolean} isInTestMode
 * @returns {*}
 */
function getHotelsRoomsCapacityIds(hotels, isInTestMode) {
  if (!hotels) {
    return [];
  }
  const capacityIds = [];
  map(hotels,
    hotel => {
      if (getIn(hotel, ['roomTypes'])) {
        map(hotel.roomTypes,
          room => {
            if (getIn(room, ['roomRate'])) {
              map(room.roomRate,
                roomDetail => {
                  if (getIn(roomDetail, ['capacityId'])) {
                    capacityIds.push(getCapacityIdBasedOnTestMode(roomDetail.capacityId, isInTestMode));
                  }
                });
            }
          });
      }
    });
  return [...capacityIds];
}

const getCapacitySummaries = async (capacityClient, accessToken, capacityIds) => {
  let capacityMap;
  if (capacityIds.length > 0) {
    capacityMap = await capacityClient.getCapacitySummaries(accessToken, capacityIds);
  }
  return capacityMap;
};

/**
 * Fetch hotel room capacity summaries from service and process the response to return a map of capacity IDs
 * with the associated available count
 * @param travelApiClient
 * @param eventId
 * @param accessToken
 * @param isInTestMode
 * @param regTypeId
 * @returns {Promise<{}>}
 */
const getHotelRoomCapacityMap = async (travelApiClient, eventId, accessToken, isInTestMode, regTypeId) => {
  let capacityMap = {};
  const capacitySummaries = await travelApiClient.getHotelRoomCapacitySummaries(eventId, accessToken,
    isInTestMode, regTypeId);

  Object.values(capacitySummaries.hotelCapacities)
    .filter(hotel => hotel.roomCapacities)
    .map(hotel => Object.values(hotel.roomCapacities))
    .flat()
    .filter(room => room.dateCapacities)
    .map(room => Object.values(room.dateCapacities))
    .flat(2)
    .filter(capacity => capacity.contactTypeId === regTypeId && capacity.capacityId
      && capacity.totalReserveCapacityAvailable)
    .forEach(capacity => {
      capacityMap[capacity.capacityId] = {
        availableCapacity: capacity.totalReserveCapacityAvailable
      };
    });

  return capacityMap;
};

/**
 * Loads the capacities from the backend and add property hasCapacity to room rates level
 * @returns {Function}
 */
export const loadHotelsRoomsCapacities = (flexProductVersion, registrationTypeId = defaultRegistrationTypeId) => {
  return async (dispatch, getState) => {
    const {
      accessToken,
      clients: { capacityClient },
      eventTravel: {
        hotelsData: {
          hotels,
          hotelRequestRules
        }
      },
      event: {
        id: eventId,
        isInTestMode
      }
    } = getState();

    /*
     * for site-editor we would not be having this userSession in state, so lets assuming it as false as we will
     * render it same as guest side.
     */
    let isPlanner = getIn(getState(), ['userSession', 'isPlanner']) || false;

    const shouldUseHotelRoomCapacityEndpoint = flexProductVersion >= 80;
    let capacityMap;
    if (shouldUseHotelRoomCapacityEndpoint) {
      const travelApiClient = getIn(getState(), ['clients', 'travelApiClient']);
      capacityMap = await getHotelRoomCapacityMap(travelApiClient, eventId, accessToken, isInTestMode,
        registrationTypeId);
    } else {
      const capacityIds = getHotelsRoomsCapacityIds(hotels, isInTestMode);
      capacityMap = await getCapacitySummaries(capacityClient, accessToken, capacityIds);
    }
    dispatch(createActionToLoadHotelsRoomsCapacities(hotels, hotelRequestRules, capacityMap, isInTestMode, isPlanner));
  };
};

/**
 * applies any necessary transformations and migrations to work with the client side application.
 * @returns {function(*, *): {type: string, payload: {hotels: Object[]}}}
 */
export const loadHotelRoomDates = () => {
  return (dispatch, getState) => {
    const {
      eventTravel: {
        hotelsData: {
          hotels,
          hotelRequestRules
        }
      },
      event: {
        timezone
      },
      timezones
    } = getState();

    dispatch(createLoadHotelRoomDatesAction(hotels, hotelRequestRules, timezones[timezone]));
  };
};

/**
 * Loads the group flight capacities from the backend and adds a property hasCapacity for each group flight
 */
export const loadGroupFlightCapacities = () => {
  return async (dispatch, getState) => {
    const {
      accessToken,
      clients: { capacityClient },
      event: {
        isInTestMode
      },
      userSession: {
        isPlanner
      } = {}
    } = getState();
    const { isGroupFlightEnabled, groupFlightSetup } = getGroupFlightsSnapshotData(getState().eventTravel);
    if (!isGroupFlightEnabled) {
      return;
    }
    const { groupFlights } = groupFlightSetup;
    let groupFlightCapacityIds = [];
    map(groupFlights,
      groupFlight => {
        groupFlightCapacityIds.push(getCapacityIdBasedOnTestMode(groupFlight.id, isInTestMode));
      });

    let capacityMap = {};
    if (!isPlanner) {
      capacityMap = await getCapacitySummaries(capacityClient, accessToken, groupFlightCapacityIds);
    }
    dispatch(createLoadGroupFlightsCapacityAction(groupFlightCapacityIds, capacityMap, isPlanner));
  };
};

/**
 * applies any necessary transformations and migrations to work with the client side application.
 * @returns {function(*, *): {type: string, payload: {airRequestSetup: Object}}}
 */
export const loadAirTravelData = () => {
  return (dispatch, getState) => {
    const {
      eventTravel: {
        airData
      }
    } = getState();
    dispatch(createLoadAirTravelDataAction(airData));
  };
};

const isTravelEnabled = ({ travelAndHousing }) => {
  return travelAndHousing && (travelAndHousing.hotelAccomodations || travelAndHousing.carRental
    || travelAndHousing.airTravel.enabled);
};

/**
 * Loads the travel snapshot from the backend and applies any necessary transformations
 * and migrations to work with the client side application.
 */
export const loadTravelSnapshotVersion = (travelSnapshotVersion) => {
  return async (dispatch, getState) => {
    const {
      event: {
        eventFeatureSetup
      }
    } = getState();
    if (isTravelEnabled(eventFeatureSetup)) {
      const {
        userSession: { eventId },
        clients: { eventSnapshotClient },
        account: { hotels },
        event: { isInTestMode }
      } = getState();
      const travelSnapshot = await eventSnapshotClient.getEventTravelSnapshot(eventId,
        { version: travelSnapshotVersion });
      dispatch(createLoadTravelSnapshotAction(travelSnapshot.travelSnapshot, travelSnapshot.travelSnapshotVersion,
        hotels, isInTestMode));
      const isHotelRequestEnabled = getIn(getState(), ['eventTravel', 'hotelsData', 'isHotelRequestEnabled']);
      if (isHotelRequestEnabled) {
        dispatch(loadHotelRoomDates());
      }
      const isAirRequestFormEnabled = getIn(getState(), ['eventTravel', 'airData', 'isAirRequestFormEnabled']);
      if (isAirRequestFormEnabled) {
        dispatch(loadAirTravelData());
      }
    }
  };
};

/**
 * Creates a thunked action to load travel snapshot into the redux store and applies any necessary transformations
 * and migrations to work with the client side application.
 * @param travelSnapshot
 * @param isTravelEnabledForEvent
 */
export const loadTravelSnapshotDataIntoStore = (travelSnapshot, isTravelEnabledForEvent) => {
  return (dispatch, getState) => {
    if (isTravelEnabledForEvent) {
      const {
        account: { hotels },
        event: { isInTestMode }
      } = getState();
      dispatch(createLoadTravelSnapshotAction(travelSnapshot.travelSnapshot, travelSnapshot.travelSnapshotVersion,
        hotels, isInTestMode));
      const isHotelRequestEnabled = getIn(getState(), ['eventTravel', 'hotelsData', 'isHotelRequestEnabled']);
      if (isHotelRequestEnabled) {
        dispatch(loadHotelRoomDates());
      }
      const isAirRequestFormEnabled = getIn(getState(), ['eventTravel', 'airData', 'isAirRequestFormEnabled']);
      if (isAirRequestFormEnabled) {
        dispatch(loadAirTravelData());
      }
    }
  };
};
